// src/auth/AuthContext.js - Modified to use portalUsers endpoint
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import ApiService from '../services/apiService';
import BusinessCentralService from '../services/businessCentralService';
import logger from '../utils/logger';

// Create the authentication context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [activeEnvironment, setActiveEnvironment] = useState(null);
  const [portalUserVerified, setPortalUserVerified] = useState(false);
  const [isPendingFirstLogin, setIsPendingFirstLogin] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [adminStatusVerified, setAdminStatusVerified] = useState(false);
  const validationInProgress = useRef(false); // prevents concurrent validateTokenWithBackend calls
  const inactivityTimer = useRef(null);
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  // On mount, check localStorage for stored user
  useEffect(() => {
    const initializeAuth = () => {
      // First check if we have a stored user in localStorage
      try {
        const storedAuth = localStorage.getItem('isAuthenticated');
        const storedUser = localStorage.getItem('currentUser');
        
        if (storedAuth === 'true' && storedUser) {
          // Parse stored user — strip isAdmin so it can only be set by a fresh backend
          // verification, never by a locally-manipulated localStorage value
          const parsedUser = JSON.parse(storedUser);
          const { isAdmin: _stripped, ...userWithoutAdmin } = parsedUser;

          // Set initial state from localStorage
          setUser(userWithoutAdmin);
          setTokenValidated(true);
          setPortalUserVerified(true);
          
          // Check for active environment
          const storedEnvironment = localStorage.getItem('activeEnvironment');
          if (storedEnvironment) {
            setActiveEnvironment(JSON.parse(storedEnvironment));
          }
          
          // Authentication state restored from localStorage
        }
      } catch (error) {
        logger.error('Error restoring auth state from localStorage:', error);
        // Clear potentially corrupt data
        localStorage.removeItem('currentUser');
      }
      
      setAuthInitialized(true);
      setIsLoading(false);
    };
    
    initializeAuth();

    // Cross-tab logout: fires in all OTHER tabs when localStorage changes.
    // When Tab A signs out and removes 'isAuthenticated', Tab B detects it here
    // and redirects to Microsoft login immediately.
    const handleStorageChange = (e) => {
      if (e.key === 'isAuthenticated' && e.newValue === null) {
        logger.log('Sign-out detected from another tab — redirecting to login');
        instance.acquireTokenRedirect(loginRequest);
      }
    };

    // API 401 handler: dispatched by apiService when backend returns 401.
    // Covers the case where the session cookie is gone but MSAL token still exists.
    const handleAuthRequired = () => {
      logger.log('Authentication required (401) — redirecting to login');
      instance.acquireTokenRedirect(loginRequest);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authRequired', handleAuthRequired);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authRequired', handleAuthRequired);
    };
  }, [instance]);

  useEffect(() => {
    const validateTokenWithBackend = async () => {
      if (!account) {
        if (!authInitialized) {
          setIsLoading(false);
        }
        return;
      }

      // Guard: prevent concurrent executions triggered by intermediate state updates
      if (validationInProgress.current) return;
      validationInProgress.current = true;
      
      if (process.env.NODE_ENV === 'development') {
        logger.log('Starting token validation with backend');
      }
      
      setIsLoading(true);
      
      try {
        // Get token silently
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: account
        });
        
        if (process.env.NODE_ENV === 'development') {
          logger.log("Token acquired successfully");
        }
        
        try {
          // First, set the basic user data from MSAL
          const basicUserData = {
            oid: account.idTokenClaims?.oid || account.localAccountId || 'unknown-id',
            email: account.username || 'unknown@example.com',
            name: account.name || 'Unknown User'
          };
          
          // Basic user data acquired from MSAL
          
          // Store this basic data in localStorage for recovery
          localStorage.setItem('currentUser', JSON.stringify(basicUserData));
          localStorage.setItem('isAuthenticated', 'true');
          
          // Set the user with basic data
          setUser(basicUserData);
          setTokenValidated(true);
          
          // Instead of using auth/verify, directly verify with portalUsers
          try {
            // Create BC service instance
            const bcService = new BusinessCentralService(() => Promise.resolve(response.accessToken));
            
            // Call portalUsers/verify directly
            const portalUserResult = await bcService.verifyPortalUser(basicUserData.email, basicUserData.oid);
            
            if (process.env.NODE_ENV === 'development') {
              logger.log("Portal user verified successfully");
            }
            
            if (portalUserResult && portalUserResult.exists) {
              if (process.env.NODE_ENV === 'development') {
                logger.log("Portal user verified in Business Central");
              }
              setPortalUserVerified(true);
              
              // Check for PendingFirstLogin status
              if (portalUserResult.userData && portalUserResult.userData.status === 'PendingFirstLogin') {
                if (process.env.NODE_ENV === 'development') {
                  logger.log("User is in PendingFirstLogin status");
                }
                setIsPendingFirstLogin(true);
                // We still allow access but flag this status
              }
              
              // Merge the basic user data with the portal user data
              if (portalUserResult.userData) {
                const enhancedUser = {
                  ...basicUserData,
                  ...portalUserResult.userData,
                  // Ensure these key fields are always present
                  id: portalUserResult.userData.id || basicUserData.oid,
                  email: portalUserResult.userData.email || basicUserData.email,
                  name: portalUserResult.userData.name || basicUserData.name,
                  isAdmin: portalUserResult.userData.isAdmin || false
                };
                
                // Enhanced user data prepared
                
                // Update user state with enhanced data (React state holds isAdmin)
                setUser(enhancedUser);
                setAdminStatusVerified(true);

                // Persist to localStorage without isAdmin — admin status is always
                // re-verified from the backend on load, never trusted from storage
                const { isAdmin: _omit, ...userForStorage } = enhancedUser;
                localStorage.setItem('currentUser', JSON.stringify(userForStorage));
                
                // Store registration ID if available
                if (portalUserResult.userData.registrationId) {
                  localStorage.setItem('registrationId', portalUserResult.userData.registrationId);
                }
                
                // Initialize the session in the backend
                try {
                  if (process.env.NODE_ENV === 'development') {
                    logger.log('Initializing session in backend after successful login');
                  }
                  
                  // Prepare session initialization data
                  const sessionData = {
                    registrationId: portalUserResult.userData.registrationId || '',
                    userEmail: enhancedUser.email || '',
                    employeeNo: '' // This will be populated later when available
                  };
                  
                  // Call the session initialization endpoint
                  const apiService = new ApiService();
                  const sessionInitResult = await apiService.initSession(sessionData);
                  
                  if (process.env.NODE_ENV === 'development') {
                    logger.log('Session initialization result:', sessionInitResult);
                  }
                } catch (sessionInitError) {
                  logger.error('Error initializing session:', sessionInitError);
                  // Continue despite session initialization error
                }
              }
              
              // Check for active environment in localStorage
              const savedEnvironment = localStorage.getItem('activeEnvironment');
              if (savedEnvironment) {
                setActiveEnvironment(JSON.parse(savedEnvironment));
              }
            } else {
              logger.error('User not registered in Business Central Portal');
              setAuthError('Your account is not registered in SiriusConnect365. Please contact your administrator.');
              
              // Clear authentication state but keep basic user info
              localStorage.removeItem('isAuthenticated');
              
              // Log out user after delay (to show error message)
              setTimeout(() => {
                logout();
              }, 15000); // 15 seconds delay to show the error
            }
          } catch (bcError) {
            logger.error("Error verifying user in Business Central:", bcError);
            
            // Handle error - no development bypasses allowed
            const errorMessage = bcError.response?.data?.message || 
                                 bcError.response?.data?.error?.message ||
                                 bcError.message ||
                                 'Failed to verify your account in Business Central';
              
            setAuthError(errorMessage);
            localStorage.removeItem('isAuthenticated');
          }
        } catch (validationError) {
          logger.error("Error during token validation:", validationError);
          
          // Clear any stored authentication
          localStorage.removeItem('isAuthenticated');
          
          setAuthError('Authentication validation failed. Please try again.');
        }
      } catch (error) {
        logger.error("Error acquiring token:", error);
        
        if (error instanceof InteractionRequiredAuthError) {
          // Fall back to redirect if silent token acquisition fails
          instance.acquireTokenRedirect(loginRequest);
        } else {
          // Clear any stored authentication
          localStorage.removeItem('isAuthenticated');
          
          setAuthError('Authentication error. Please try again.');
        }
      } finally {
        setIsLoading(false);
        validationInProgress.current = false;
      }
    };

   // Run validation when account changes, on initial load, or when admin status
   // hasn't been verified yet from the backend (happens after localStorage restore
   // which strips isAdmin for security — adminStatusVerified prevents re-entry loops)
  if (account && authInitialized && (!user || user.oid !== account.idTokenClaims?.oid || !adminStatusVerified)) {
    validateTokenWithBackend();
  }
}, [account, instance, user, authInitialized, adminStatusVerified]);

  // Inactivity auto-logout after 5 minutes
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        logger.log('User inactive for 5 minutes — logging out');
        logout();
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer(); // start the timer immediately on login

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(inactivityTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Function to acquire token for API calls
  const getAccessToken = async (scopes) => {
    if (!instance) {
      throw new Error('MSAL instance not available');
    }

    // useAccount() may return null briefly after a browser refresh while the hook hydrates.
    // Fall back to getAllAccounts() which reads directly from MSAL's cache and is always current.
    const activeAccount = account || instance.getAllAccounts()[0];

    if (!activeAccount) {
      // getAllAccounts() is synchronous — if both useAccount and getAllAccounts() return
      // nothing, the session is genuinely gone. Redirect to login.
      instance.acquireTokenRedirect(loginRequest);
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        scopes: scopes || loginRequest.scopes,
        account: activeAccount
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Fallback to interaction for token acquisition
        instance.acquireTokenRedirect({
          scopes: scopes || loginRequest.scopes
        });
      }

      throw error;
    }
  };
  
 // Method to set the active environment
const updateActiveEnvironment = (environment) => {
  if (!environment || !environment.id) {
    logger.error('Invalid environment provided:', environment);
    return;
  }

  setActiveEnvironment(environment);
  localStorage.setItem('activeEnvironment', JSON.stringify(environment));
  if (process.env.NODE_ENV === 'development') {
    logger.log('Environment activated');
  }
};
  
// Method to clear the active environment (used during logout)
const clearActiveEnvironment = () => {
  setActiveEnvironment(null);
  localStorage.removeItem('activeEnvironment');
  if (process.env.NODE_ENV === 'development') {
    logger.log('Environment selection cleared');
  }
};

  // Update status after first login
  const updateFirstLoginStatus = () => {
    if (isPendingFirstLogin) {
      setIsPendingFirstLogin(false);
      // Here you could also call an API to update the status in BC if needed
    }
  };

  // Method to handle logout with thorough cleanup
  const logout = () => {
    if (process.env.NODE_ENV === 'development') {
      logger.log('Performing logout');
    }

    // Capture account details FIRST — before any sessionStorage cleanup wipes MSAL's
    // in-memory cache. If we clear sessionStorage first, getAllAccounts() returns []
    // and logoutHint becomes undefined, causing Microsoft to show the account picker.
    const currentAccount = instance
      ? (instance.getActiveAccount() || instance.getAllAccounts()[0])
      : null;
    const logoutHint = currentAccount?.idTokenClaims?.login_hint
      || currentAccount?.idTokenClaims?.sid
      || currentAccount?.username;

    // Clear internal state
    setUser(null);
    setTokenValidated(false);
    setPortalUserVerified(false);
    setIsPendingFirstLogin(false);
    setAdminStatusVerified(false);
    validationInProgress.current = false;
    clearActiveEnvironment();

    // Clear localStorage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('activeEnvironment');
    localStorage.removeItem('registrationId');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('clientCompanies');
    localStorage.removeItem('clientCompanyId');
    localStorage.removeItem('clientCompanyName');
    localStorage.removeItem('employeeData');
    localStorage.removeItem('employeeId');
    localStorage.removeItem('employeeNo');

    // Clear any other app-specific localStorage items
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('bc_') || key.includes('auth') || key.includes('user') || key.includes('token')) {
        localStorage.removeItem(key);
      }
    });

    // Clear cookies related to authentication
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      if (name.includes('msal') || name.includes('auth') || name.includes('session')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });

    // Let MSAL's logoutRedirect handle sessionStorage cleanup — do NOT manually
    // wipe sessionStorage before this call or MSAL loses the account context.
    if (instance) {
      if (process.env.NODE_ENV === 'development') {
        logger.log('Logging out via MSAL, logoutHint:', logoutHint);
      }
      instance.logoutRedirect({
        account: currentAccount,
        logoutHint,
        postLogoutRedirectUri: window.location.origin,
      });
    } else {
      window.location.href = '/';
    }
  };

  const value = {
    user,
    isAuthenticated: (isAuthenticated && tokenValidated && portalUserVerified) || 
                     (localStorage.getItem('isAuthenticated') === 'true' && user), // Check localStorage as fallback
    isLoading,
    authError,
    isPendingFirstLogin,
    updateFirstLoginStatus,
    getAccessToken,
    activeEnvironment,
    updateActiveEnvironment,
    clearActiveEnvironment,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};